const CANONICAL_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SCHEDULED_OBJECT_TYPES = new Set(['EVENT', 'SESSION']);
const RSVP_STATUSES = new Set(['GOING', 'INTERESTED', 'PENDING', 'DECLINED', 'LEFT']);
const SCHEDULED_STATUSES = new Set(['DRAFT', 'PUBLISHED', 'CANCELLED', 'COMPLETED']);
const JOIN_POLICIES = new Set(['OPEN', 'APPROVAL_REQUIRED', 'CLOSED']);

function readTrimmedString(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim();
}

function toNullableString(value) {
    const normalized = readTrimmedString(value);
    return normalized || null;
}

function normalizeUpperString(value) {
    const normalized = readTrimmedString(value);
    return normalized ? normalized.toUpperCase() : '';
}

function hasOwnField(source, key) {
    return Boolean(source)
        && typeof source === 'object'
        && Object.prototype.hasOwnProperty.call(source, key);
}

function toInteger(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.max(0, Math.floor(value));
    }
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number.parseInt(value, 10);
        if (Number.isFinite(parsed)) {
            return Math.max(0, parsed);
        }
    }
    return 0;
}

function normalizeOptionalNonNegativeInteger(value, fallbackValue = null) {
    if (value === undefined || value === null || value === '') {
        if (fallbackValue === undefined || fallbackValue === null || fallbackValue === '') {
            return null;
        }
        return toInteger(fallbackValue);
    }
    return toInteger(value);
}

function normalizeAttendanceSummary(summary) {
    if (!summary || typeof summary !== 'object') {
        return {
            going: 0,
            interested: 0,
            pending: 0,
        };
    }
    return {
        going: toInteger(summary.going),
        interested: toInteger(summary.interested),
        pending: toInteger(summary.pending),
    };
}

function normalizeOrganizer(organizer) {
    if (!organizer || typeof organizer !== 'object') {
        return null;
    }
    const objectType = normalizeUpperString(organizer.objectType);
    const objectId = toNullableString(organizer.objectId);
    if (!objectType || !objectId || !isCanonicalObjectId(objectId)) {
        return null;
    }
    return { objectType, objectId };
}

function normalizeVenueSummary(venue) {
    if (!venue || typeof venue !== 'object') {
        return null;
    }
    const objectId = toNullableString(venue.objectId);
    if (!objectId || !isCanonicalObjectId(objectId)) {
        return null;
    }
    return {
        objectId,
        name: toNullableString(venue.name),
        sourceType: normalizeUpperString(venue.sourceType),
        inactive: toBoolean(venue.inactive),
        inactiveAt: toNullableString(venue.inactiveAt),
    };
}

function toBoolean(value) {
    return value === true;
}

export function hasOrganizerInviteCount(value) {
    return Boolean(value)
        && typeof value === 'object'
        && Object.prototype.hasOwnProperty.call(value, 'organizerInviteCount');
}

export function isCanonicalObjectId(value) {
    if (typeof value !== 'string') {
        return false;
    }
    return CANONICAL_UUID_REGEX.test(value.trim());
}

export function normalizeScheduledObjectType(value) {
    const normalized = normalizeUpperString(value);
    return SCHEDULED_OBJECT_TYPES.has(normalized) ? normalized : null;
}

export function isScheduledObjectType(value) {
    return normalizeScheduledObjectType(value) != null;
}

export function normalizeAttendanceState(payload, fallback = {}) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const objectType = normalizeScheduledObjectType(payload.objectType ?? fallback.objectType);
    const objectId = toNullableString(payload.id ?? payload.objectId ?? fallback.objectId);
    if (!objectType || !objectId || !isCanonicalObjectId(objectId)) {
        return null;
    }

    const viewerStatusCandidate = normalizeUpperString(payload.viewerAttendanceStatus);
    const viewerAttendanceStatus = RSVP_STATUSES.has(viewerStatusCandidate)
        ? viewerStatusCandidate
        : null;

    const scheduledStatusCandidate = normalizeUpperString(payload.scheduledStatus ?? payload.status ?? fallback.scheduledStatus);
    const scheduledStatus = SCHEDULED_STATUSES.has(scheduledStatusCandidate)
        ? scheduledStatusCandidate
        : null;

    const joinPolicyCandidate = normalizeUpperString(payload.joinPolicy ?? fallback.joinPolicy);
    const joinPolicy = JOIN_POLICIES.has(joinPolicyCandidate)
        ? joinPolicyCandidate
        : null;

    return {
        id: objectId,
        objectType,
        scheduledStatus,
        joinPolicy,
        attendanceSummary: normalizeAttendanceSummary(payload.attendanceSummary),
        viewerAttendanceStatus,
        viewerInvitedByOrganizer: toBoolean(payload.viewerInvitedByOrganizer),
        organizerInviteCount: normalizeOptionalNonNegativeInteger(
            payload.organizerInviteCount,
            fallback.organizerInviteCount,
        ),
        viewerRespondedAt: toNullableString(payload.viewerRespondedAt),
    };
}

export function resolveActiveRsvpStatus(status) {
    const normalized = normalizeUpperString(status);
    if (!RSVP_STATUSES.has(normalized)) {
        return null;
    }
    if (normalized === 'PENDING') {
        return 'GOING';
    }
    return normalized;
}

export function canDirectlyJoinScheduledEntity(source) {
    const scheduledStatus = normalizeUpperString(source?.scheduledStatus ?? source?.status);
    if (scheduledStatus !== 'PUBLISHED') {
        return false;
    }

    const joinPolicy = normalizeUpperString(source?.joinPolicy);
    if (joinPolicy !== 'CLOSED') {
        return true;
    }

    return source?.viewerInvitedByOrganizer === true
        && normalizeUpperString(source?.viewerAttendanceStatus) === 'PENDING';
}

export function isAwaitingScheduledEntityApproval(source) {
    const scheduledStatus = normalizeUpperString(source?.scheduledStatus ?? source?.status);
    if (scheduledStatus !== 'PUBLISHED') {
        return false;
    }

    return normalizeUpperString(source?.joinPolicy) === 'APPROVAL_REQUIRED'
        && normalizeUpperString(source?.viewerAttendanceStatus) === 'PENDING';
}

export function mergeAttendanceState(...sources) {
    const normalizedSources = new Array(sources.length);
    let fallback = null;

    for (let index = sources.length - 1; index >= 0; index -= 1) {
        const fallbackForSource = hasOrganizerInviteCount(sources[index])
            ? { ...(fallback || {}), organizerInviteCount: undefined }
            : (fallback || {});
        const normalized = normalizeAttendanceState(sources[index], fallbackForSource);
        normalizedSources[index] = normalized;
        if (normalized) {
            fallback = normalized;
        }
    }

    return normalizedSources.find(Boolean) || null;
}

export function normalizeScheduledEntityDetails(payload, fallback = {}) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const objectType = normalizeScheduledObjectType(payload.objectType ?? fallback.objectType);
    const objectId = toNullableString(payload.id ?? fallback.objectId);
    if (!objectType || !objectId || !isCanonicalObjectId(objectId)) {
        return null;
    }

    const joinPolicyCandidate = normalizeUpperString(payload.joinPolicy);
    const joinPolicy = JOIN_POLICIES.has(joinPolicyCandidate)
        ? joinPolicyCandidate
        : null;

    const statusCandidate = normalizeUpperString(payload.status);
    const status = SCHEDULED_STATUSES.has(statusCandidate)
        ? statusCandidate
        : null;

    const location = payload.location && typeof payload.location === 'object'
        ? payload.location
        : {};

    return {
        id: objectId,
        objectType,
        title: toNullableString(payload.title ?? fallback.title),
        description: toNullableString(payload.description),
        status,
        visibility: toNullableString(payload.visibility ?? fallback.visibility),
        timezone: toNullableString(payload.timezone),
        startAt: toNullableString(payload.startAt ?? fallback.startAt),
        endAt: toNullableString(payload.endAt ?? fallback.endAt),
        joinPolicy,
        attendeeLimit: typeof payload.attendeeLimit === 'number' ? payload.attendeeLimit : null,
        locationName: toNullableString(location.locationName),
        addressLine: toNullableString(location.addressLine),
        city: toNullableString(location.city ?? fallback.city),
        stateOrRegion: toNullableString(location.stateOrRegion),
        country: toNullableString(location.country),
        countryCode: toNullableString(location.countryCode),
        latitude: typeof location.latitude === 'number' ? location.latitude : null,
        longitude: typeof location.longitude === 'number' ? location.longitude : null,
        organizer: normalizeOrganizer(hasOwnField(payload, 'organizer') ? payload.organizer : fallback.organizer),
        venue: normalizeVenueSummary(hasOwnField(payload, 'venue') ? payload.venue : fallback.venue),
        attendanceSummary: normalizeAttendanceSummary(payload.attendanceSummary),
        viewerAttendanceStatus: toNullableString(payload.viewerAttendanceStatus),
        viewerInvitedByOrganizer: toBoolean(payload.viewerInvitedByOrganizer),
        organizerInviteCount: normalizeOptionalNonNegativeInteger(
            payload.organizerInviteCount,
            fallback.organizerInviteCount,
        ),
        updatedAt: toNullableString(payload.updatedAt),
        createdAt: toNullableString(payload.createdAt),
    };
}
